import React, { useState, useEffect } from 'react';
import { Cookie, X, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Check if user has already accepted cookies
    const cookieConsent = localStorage.getItem('cookie_consent');
    if (!cookieConsent) {
      // Small delay before showing banner for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
        setTimeout(() => setIsAnimating(true), 50);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem('cookie_consent', JSON.stringify({
      accepted: true,
      date: new Date().toISOString()
    }));
    setIsAnimating(false);
    setTimeout(() => setIsVisible(false), 300);
  };

  const declineCookies = () => {
    localStorage.setItem('cookie_consent', JSON.stringify({
      accepted: false,
      date: new Date().toISOString()
    }));
    setIsAnimating(false);
    setTimeout(() => setIsVisible(false), 300);
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`fixed bottom-0 left-0 right-0 z-50 p-4 transition-all duration-300 ${
        isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
      data-testid="cookie-banner"
    >
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Icon */}
            <div className="hidden sm:flex w-12 h-12 bg-[#f87171]/10 rounded-xl items-center justify-center flex-shrink-0">
              <Cookie className="w-6 h-6 text-[#f87171]" />
            </div>
            
            {/* Content */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Cookie className="w-5 h-5 text-[#f87171] sm:hidden" />
                <h3 className="font-semibold text-[#1a365d]">Piškotki in zasebnost</h3>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">
                Spletna stran uporablja nujne piškotke za delovanje ter piškotke za shranjevanje vaših nastavitev. 
                S klikom na "Sprejmi" se strinjate z uporabo piškotkov. 
                <a href="/zasebnost" className="text-[#f87171] hover:underline ml-1">Več o zasebnosti</a>
              </p>
            </div>
            
            {/* Buttons */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={declineCookies}
                className="flex-1 sm:flex-none rounded-full text-slate-600 border-slate-300 hover:bg-slate-100"
                data-testid="cookie-decline"
              >
                Zavrni
              </Button>
              <Button
                size="sm"
                onClick={acceptCookies}
                className="flex-1 sm:flex-none bg-[#f87171] hover:bg-[#f87171]/90 text-white rounded-full px-6"
                data-testid="cookie-accept"
              >
                Sprejmi
              </Button>
            </div>
          </div>
          
          {/* Trust badge */}
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
            <Shield className="w-4 h-4 text-green-600" />
            <span className="text-xs text-slate-500">Vaši podatki so varni in se obdelujejo skladno z GDPR.</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
